import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import useRealtimeConnection from '../../hooks/useRealtimeConnection';
import { formatDurationCompact } from '../../lib/formatters/time';

/**
 * Stale-connection banner (S7.1/S7.2) — after the 5-attempt reconnect cap
 * (or a silent backend) the dashboard must never sit on stale data silently:
 * the banner names the state, shows heartbeat age, and offers a manual
 * reconnect that resubscribes topics without a page reload. Rendered by both
 * persona layouts so every route is covered.
 *
 * Dismiss hides the banner for the current degraded episode only; when the
 * episode key changes (state flips or the heartbeat ages another minute) the
 * banner returns — silence is never permanent while data is stale.
 */
function ReconnectBanner() {
  const {
    connectionStatus,
    heartbeatAgeMs,
    requestReconnect
  } = useRealtimeConnection();

  const [dismissedEpisode, setDismissedEpisode] = useState(null);
  // Cutover §3: the banner exists for DEGRADED episodes of a real transport.
  // 'no-transport' (the boot state this build ships in — no WS/SSE feed
  // exists) is not degraded, it is the honest absence of a feed; alarming
  // every route with a banner over it would be noise, not honesty. Visible
  // states stay stale/disconnected for when a real transport is mounted (B7).
  const visible = connectionStatus === 'stale' || connectionStatus === 'disconnected';
  const episodeKey = `${connectionStatus}:${Math.floor((heartbeatAgeMs ?? 0) / 60000)}`;

  useEffect(() => {
    setDismissedEpisode(null);
  }, [episodeKey]);

  if (!visible || dismissedEpisode === episodeKey) return null;

  const copy = connectionStatus === 'stale'
    ? {
        title: 'Connection degraded',
        body: `Live updates are not arriving (last heartbeat ${
          heartbeatAgeMs === null ? 'unknown' : `${formatDurationCompact(heartbeatAgeMs)} ago`
        }). Data on screen may be outdated.`
      }
    : {
        title: 'Connection lost',
        body: `Automatic reconnection stopped after 5 attempts. Data on screen is frozen${
          heartbeatAgeMs === null ? '' : ` (last heartbeat ${formatDurationCompact(heartbeatAgeMs)} ago)`
        }.`
      };

  return (
    <div className={`reconnect-banner reconnect-banner--${connectionStatus}`} role="alert">
      <span className="reconnect-banner__icon"><FiIcons.FiWifiOff /></span>
      <div className="reconnect-banner__text">
        <strong>{copy.title}</strong>
        <span>{copy.body}</span>
      </div>
      <button className="reconnect-banner__action" onClick={requestReconnect} type="button">
        Reconnect now
      </button>
      <button
        aria-label="Dismiss stale-connection banner"
        className="reconnect-banner__dismiss"
        onClick={() => setDismissedEpisode(episodeKey)}
        type="button"
      >
        <FiIcons.FiX />
      </button>
    </div>
  );
}

export default ReconnectBanner;
